import { jsPDF } from "jspdf";
import "jspdf-autotable";
import QRCode from "qrcode";

// Job card (JOBCARD ISSUE) PDF builder. Mirrors the single-order layout used
// on the order list / order details screens, but takes a list of orders so a
// whole karigar's issue can be produced as one file.

const capitalizeFirst = (text) =>
  !text || typeof text !== "string"
    ? text
    : text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date)) return "Invalid Date";
  return `${String(date.getDate()).padStart(2, "0")}-${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}-${date.getFullYear()}`;
};

const isImageUrl = (url) =>
  ["jpg", "jpeg", "png", "gif", "webp"].some((ext) =>
    url.toLowerCase().includes(`.${ext}`),
  );

const convertToCompressedBase64 = async (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxDim = 1000;
      let { width, height } = img;

      if (width > maxDim || height > maxDim) {
        const scale = Math.min(maxDim / width, maxDim / height);
        width = width * scale;
        height = height * scale;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = reject;
    img.src = url;
  });

// Draws one job card onto the current page of `doc`.
const drawJobCard = async (doc, order, storeName) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = 1;

  // doc.setFont("helvetica", "bold");
  // doc.setFontSize(20);
  // doc.text(storeName || "Store", pageWidth / 2, 12, { align: "center" });

  // doc.setFontSize(11);
  // doc.setFont("helvetica", "normal");
  // doc.text("( JOBCARD ISSUE )", pageWidth / 2, 20, { align: "center" });

  // doc.setDrawColor(0);
  // doc.setLineWidth(0.3);
  // doc.line(0, headerHeight + 1, pageWidth, headerHeight + 1);

  let qrDataUrl = "";
  try {
    qrDataUrl = await QRCode.toDataURL(order.storeorderno || "", {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 60,
    });
  } catch (err) {
    console.warn("QR generation failed", err);
  }

  let metaY = headerHeight + 10;
  const addLabelValue = (label, value) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`${label}:`, 8, metaY);
    const labelWidth = doc.getTextWidth(`${label}:`);
    doc.setFont("helvetica", "normal");
    doc.text(`${value ?? ""}`, 8 + labelWidth + 2, metaY);
    metaY += 6;
  };

  addLabelValue("Order No", order.storeorderno);
  addLabelValue("Order Date", formatDateToDDMMYYYY(order.orderdate));
  addLabelValue("Karigar", order.karigar_name);
  // if (order.customer_order_number) {
  //   addLabelValue("Exhibition Order No", order.customer_order_number);
  // }
  // if (order.customer_name) {
  //   addLabelValue("Customer", order.customer_name);
  // }

  if (qrDataUrl) {
    const qrSize = 25;
    doc.addImage(
      qrDataUrl,
      "PNG",
      pageWidth - qrSize - 10,
      headerHeight + 4,
      qrSize,
      qrSize,
    );
  }

  const tableY = Math.max(metaY, headerHeight + 34);
  doc.autoTable({
    startY: tableY,
    body: [
      [
        "Item",
        capitalizeFirst(order.itemtype_name),
        "Purity",
        order.purity,
        "",
        "",
      ],
      [
        "Design",
        capitalizeFirst(order.design_name),
        "PCS",
        order.pcs,
        "Size",
        order.size_len,
      ],
      ["Gross Wt", order.weight, "Net Wt", order.netwt, "", ""],
      ["Description", { content: order.description || "", colSpan: 5 }],
    ],
    theme: "grid",
    styles: { fontSize: 10, textColor: [40, 40, 40] },
    columnStyles: {
      0: { cellWidth: 27 },
      1: { cellWidth: 38 },
      2: { cellWidth: 27 },
      3: { cellWidth: 38 },
      4: { cellWidth: 27 },
      5: { cellWidth: 38 },
    },
    margin: { left: 5, right: 5 },
  });

  let contentY = doc.lastAutoTable.finalY + 10;

  // doc.setFont("helvetica", "bold");
  // doc.setTextColor(255, 0, 0);
  // doc.text(
  //   `Expected Date: ${formatDateToDDMMYYYY(order.karigar_delivery_date)}`,
  //   pageWidth / 2,
  //   contentY,
  //   { align: "center" },
  // );
  // doc.setTextColor(0, 0, 0);
  // contentY += 10;

  // Only the first image goes on the job card page; the rest are ignored so
  // one order stays one page in a multi-order karigar issue.
  const firstImage = (order.images || []).find(
    (file) => file && isImageUrl(file),
  );

  if (firstImage) {
    try {
      const base64 = await convertToCompressedBase64(firstImage);
      const { width, height } = doc.getImageProperties(base64);

      let imgH = 130;
      let imgW = (imgH / height) * width;
      if (width > height) {
        imgW = 170;
        imgH = (imgW / width) * height;
      }

      doc.addImage(
        base64,
        "JPEG",
        (pageWidth - imgW) / 2,
        contentY,
        imgW,
        imgH,
      );
    } catch (err) {
      console.warn("Failed to load job card image:", firstImage, err);
    }
  }
};

/**
 * Builds a single PDF holding one job card page per order.
 * @returns {Promise<Blob>}
 */
export const buildJobCardPdf = async (orders, storeName) => {
  const doc = new jsPDF();

  for (let i = 0; i < orders.length; i++) {
    if (i > 0) doc.addPage();
    // eslint-disable-next-line no-await-in-loop
    await drawJobCard(doc, orders[i], storeName);
  }

  return doc.output("blob");
};

const sanitiseFileName = (name) =>
  String(name || "karigar")
    .trim()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "") || "karigar";

/**
 * Groups the created orders by karigar and downloads one job card PDF per
 * karigar. Returns the file names that were produced.
 */
export const downloadJobCardsByKarigar = async (orders, storeName) => {
  // Grouped case-insensitively so a karigar named from the stock catalogue on
  // one row and from the CSV on another still lands in a single file.
  const byKarigar = orders.reduce((acc, order) => {
    const name = order.karigar_name || "Unassigned";
    const key = name.trim().toLowerCase();
    if (!acc[key]) acc[key] = { name, orders: [] };
    acc[key].orders.push(order);
    return acc;
  }, {});

  const fileNames = [];

  for (const { name, orders: karigarOrders } of Object.values(byKarigar)) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await buildJobCardPdf(karigarOrders, storeName);
    const fileName = `jobcards_${sanitiseFileName(name)}.pdf`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);

    fileNames.push(fileName);
  }

  return fileNames;
};
